// src/utils/secureStore.js
// Small IndexedDB key/value helper for sensitive client-side records.
// Used for PIN verifier metadata and other device-local data that should not
// live in plain localStorage. Legacy localStorage values are read only for
// one-time migration and then removed.

import { logWarn } from "./logger";

const DB_NAME = "finvault_secure_client_store_v1";
const STORE_NAME = "kv";
let dbPromise = null;

const canUseIndexedDB = () => typeof indexedDB !== "undefined";
const legacyStorage = () => {
  try { return typeof window !== "undefined" ? window["localStorage"] : null; }
  catch { return null; }
};

function openDb() {
  if (!canUseIndexedDB()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not open secure store."));
  }).catch((err) => {
    logWarn("FinVault secure store unavailable", err);
    return null;
  });
  return dbPromise;
}

export async function secureStoreAvailable() {
  return Boolean(await openDb());
}

export async function getSecureItem(key) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(String(key));
    req.onsuccess = () => resolve(req.result?.value ?? null);
    req.onerror = () => reject(req.error || new Error("Secure store read failed."));
  });
}

export async function setSecureItem(key, value) {
  const db = await openDb();
  if (!db) throw new Error("Secure device storage is unavailable.");
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({ v: 1, value, updatedAt: new Date().toISOString() }, String(key));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error("Secure store write failed."));
  });
}

export async function removeSecureItem(key) {
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(String(key));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error("Secure store delete failed."));
  });
}

export async function migrateLegacyJsonItem(key, legacyKey) {
  const existing = await getSecureItem(key);
  if (existing) return existing;
  const store = legacyStorage();
  if (!store) return null;
  let parsed = null;
  try { parsed = JSON.parse(store.getItem(legacyKey) || "null"); } catch { parsed = null; }
  if (parsed) {
    await setSecureItem(key, parsed);
    try { store.removeItem(legacyKey); } catch {}
  }
  return parsed;
}

export function readLegacyString(legacyKey) {
  const store = legacyStorage();
  try { return store?.getItem(legacyKey) || ""; } catch { return ""; }
}

export function removeLegacyItem(legacyKey) {
  const store = legacyStorage();
  try { store?.removeItem(legacyKey); } catch {}
}
